import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SuiteCard, SuiteButton } from "@/components/suite";
import {
  Search, X, Plus, Pencil, Trash2, Upload,
  ChevronRight, ChevronDown, Lock, Globe, Building2,
} from "lucide-react";

/**
 * Catalogo Causali — layout gerarchico a due colonne.
 * v0.99.84 UI-only redesign. Nessuna modifica a schema DB, API, permessi.
 *
 * Il componente riceve dal parent (Impostazioni.jsx):
 *   causes             elenco già filtrato dal backend per il ruolo/azienda attivi
 *   canManageCause(c)  callback esistente per verificare i permessi di gestione
 *   isSuper, isCompanyAdmin, isCompanyRole  flag ruolo del parent
 *   openNew, openEdit(c), toggleActive(c), toggleClient(c), remove(c), promoteGlobal(c)
 *   effectiveRangeDayMode(c), rangeDayModeLabels
 */

// Etichette categorie richieste dall'utente + fallback legacy per record storici.
const CATEGORY_ORDER = [
  { key: "lavoro",                 label: "Lavoro" },
  { key: "assenze_retribuite",     label: "Assenze retribuite" },
  { key: "assenze_giustificate",   label: "Assenze giustificate" },
  { key: "assenze_non_retribuite", label: "Assenze non retribuite" },
  { key: "congedi",                label: "Congedi" },
];
const LEGACY_TO_NEW = {
  worked: "lavoro",
  justified: "assenze_giustificate",
  unjustified: "assenze_non_retribuite",
};
function categoryOf(cause) {
  const raw = cause?.category || "assenze_giustificate";
  return LEGACY_TO_NEW[raw] || raw;
}
function categoryLabel(key) {
  const found = CATEGORY_ORDER.find((c) => c.key === key);
  return found ? found.label : key;
}

// Provenienza — un solo posto di verità.
function provenanceOf(cause) {
  if (cause?.system) return "system";
  if ((cause?.scope || "global") === "company") return "company";
  return "global";
}
const PROVENANCE_META = {
  system:  { label: "Sistema",   icon: Lock,       badge: "bg-rose-50 text-rose-700 border-rose-100" },
  global:  { label: "Globale",   icon: Globe,      badge: "bg-indigo-50 text-indigo-700 border-indigo-100" },
  company: { label: "Aziendale", icon: Building2,  badge: "bg-emerald-50 text-emerald-700 border-emerald-100" },
};

// case+accent insensitive normalization.
function normalizeSearch(raw) {
  return String(raw || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().trim();
}

// Etichette compatte "regole" (badge). Ordine: le prime 3 mostrate, le altre in +N.
function ruleBadges(cause, rangeDayModeLabels, effectiveRangeDayMode) {
  const b = cause?.behavior || {};
  const badges = [];
  if (b.counts_as_work)      badges.push({ key: "work",   text: "lavoro",         css: "bg-emerald-50 text-emerald-700 border-emerald-100" });
  if (b.reduces_theoretical) badges.push({ key: "teoric", text: "riduce teorico", css: "bg-amber-50 text-amber-700 border-amber-100" });
  if (b.affects_bank)        badges.push({ key: "bank",   text: "banca ore",      css: "bg-sky-50 text-sky-700 border-sky-100" });
  if (b.auto_computed)       badges.push({ key: "auto",   text: "auto",           css: "bg-violet-50 text-violet-700 border-violet-100" });
  if (b.requires_note)       badges.push({ key: "note",   text: "nota obbl.",     css: "bg-slate-100 text-slate-600 border-slate-200" });
  if (b.max_hours_per_day)   badges.push({ key: "max",    text: `max ${b.max_hours_per_day}h`, css: "bg-rose-50 text-rose-700 border-rose-100" });
  if (b.studio_only)         badges.push({ key: "studio", text: "studio",         css: "bg-slate-800 text-white border-slate-800" });
  badges.push({
    key: "range",
    text: rangeDayModeLabels[effectiveRangeDayMode(cause)] || "gg lavorativi",
    css: "bg-blue-50 text-blue-700 border-blue-100",
  });
  return badges;
}

export default function CausesCatalog({
  causes,
  canManageCause,
  isSuper,
  isCompanyAdmin,
  openNew,
  openEdit,
  toggleActive,
  toggleClient,
  remove,
  promoteGlobal,
  effectiveRangeDayMode,
  rangeDayModeLabels,
}) {
  // Stati locali della UI
  const [search, setSearch] = useState("");
  const [provenanceFilter, setProvenanceFilter] = useState("all"); // all | system | global | company
  const [statusFilter, setStatusFilter] = useState("all");         // all | active | suspended
  const [selectedCategory, setSelectedCategory] = useState(null);  // null = "Tutte"
  const [expanded, setExpanded] = useState(() => new Set());       // categorie aperte
  const [selectedCauseId, setSelectedCauseId] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Cache scroll-to per evidenziare una riga cercata
  const rowRefs = useRef(new Map());

  // Raggruppamento per categoria
  const grouped = useMemo(() => {
    const g = {};
    for (const c of causes) {
      const k = categoryOf(c);
      (g[k] = g[k] || []).push(c);
    }
    // ordine interno per label
    for (const k of Object.keys(g)) g[k].sort((a, b) => (a.order || 100) - (b.order || 100));
    return g;
  }, [causes]);

  // Filtri combinati che si applicano alla tabella destra
  const filteredForTable = useMemo(() => {
    const q = normalizeSearch(search);
    return causes.filter((c) => {
      // Se l'utente sta cercando, la categoria selezionata non deve escludere risultati
      // provenienti da altre categorie (altrimenti la ricerca sembra "non trovare nulla").
      if (!q && selectedCategory && categoryOf(c) !== selectedCategory) return false;
      if (provenanceFilter !== "all" && provenanceOf(c) !== provenanceFilter) return false;
      if (statusFilter === "active" && !c.active) return false;
      if (statusFilter === "suspended" && c.active) return false;
      if (q) {
        const hay = normalizeSearch([c.label, c.display_code || c.code, categoryLabel(categoryOf(c))].join(" "));
        if (!hay.includes(q)) return false;
      }
      return true;
    }).sort((a, b) => {
      const ca = categoryOf(a), cb = categoryOf(b);
      const ia = CATEGORY_ORDER.findIndex((x) => x.key === ca);
      const ib = CATEGORY_ORDER.findIndex((x) => x.key === cb);
      if (ia !== ib) return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      return (a.order || 100) - (b.order || 100);
    });
  }, [causes, search, provenanceFilter, statusFilter, selectedCategory]);

  // Ricerca live: apre automaticamente le categorie con match
  useEffect(() => {
    const q = normalizeSearch(search);
    if (!q) return;
    const hits = new Set();
    for (const c of causes) {
      const hay = normalizeSearch([c.label, c.display_code || c.code, categoryLabel(categoryOf(c))].join(" "));
      if (hay.includes(q)) hits.add(categoryOf(c));
    }
    setExpanded((prev) => {
      const next = new Set(prev);
      hits.forEach((k) => next.add(k));
      return next;
    });
    // seleziona/evidenzia il primo risultato
    const firstMatch = causes.find((c) => {
      const hay = normalizeSearch([c.label, c.display_code || c.code, categoryLabel(categoryOf(c))].join(" "));
      return hay.includes(q);
    });
    if (firstMatch) setSelectedCauseId(firstMatch.id);
  }, [search, causes]);

  // Scroll-to selected
  useEffect(() => {
    if (!selectedCauseId) return;
    const el = rowRefs.current.get(selectedCauseId);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedCauseId]);

  // Espansione categorie
  const toggleCat = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const expandAll = () => setExpanded(new Set(CATEGORY_ORDER.map((c) => c.key)));
  const collapseAll = () => setExpanded(new Set());

  // Handler categoria: click sul nome filtra, click sulla freccia solo espande
  const handleCategoryClick = (key) => {
    setSelectedCategory((prev) => (prev === key ? key : key));
  };
  const handleAllCauses = () => {
    setSelectedCategory(null);
  };

  // Rende la nav sinistra
  const NavTree = (
    <nav className="text-sm" aria-label="Categorie causali">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Categorie</span>
        <div className="flex gap-2 text-[11px]">
          <button
            type="button"
            onClick={expandAll}
            className="text-slate-500 hover:text-slate-800 underline underline-offset-2"
            data-testid="cause-expand-all"
          >Espandi tutto</button>
          <span className="text-slate-300">·</span>
          <button
            type="button"
            onClick={collapseAll}
            className="text-slate-500 hover:text-slate-800 underline underline-offset-2"
            data-testid="cause-collapse-all"
          >Comprimi tutto</button>
        </div>
      </div>

      <ul className="space-y-0.5">
        <li>
          <button
            type="button"
            onClick={handleAllCauses}
            data-testid="cause-nav-all"
            aria-current={selectedCategory === null ? "true" : undefined}
            className={`w-full text-left px-2 py-1.5 rounded-md flex items-center gap-2 text-sm ${
              selectedCategory === null
                ? "bg-suite-primary/10 text-suite-primary font-semibold"
                : "text-slate-700 hover:bg-slate-100"
            }`}
          >
            <span className="w-4 shrink-0" />
            Tutte le causali
          </button>
        </li>

        {CATEGORY_ORDER.map((cat) => {
          const isOpen = expanded.has(cat.key);
          const isSel = selectedCategory === cat.key;
          const children = grouped[cat.key] || [];
          return (
            <li key={cat.key}>
              <div className={`flex items-center gap-1 pr-1 rounded-md ${
                isSel ? "bg-suite-primary/10" : "hover:bg-slate-100"
              }`}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleCat(cat.key); }}
                  className="p-1 text-slate-500 hover:text-slate-800 rounded"
                  aria-expanded={isOpen}
                  aria-label={isOpen ? `Comprimi ${cat.label}` : `Espandi ${cat.label}`}
                  data-testid={`cause-nav-toggle-${cat.key}`}
                >
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => handleCategoryClick(cat.key)}
                  data-testid={`cause-nav-cat-${cat.key}`}
                  aria-current={isSel ? "true" : undefined}
                  className={`flex-1 text-left px-1 py-1.5 text-sm ${
                    isSel ? "text-suite-primary font-semibold" : "text-slate-700"
                  }`}
                >
                  {cat.label}
                </button>
              </div>
              {isOpen && children.length > 0 && (
                <ul className="ml-6 space-y-0.5 border-l border-slate-200 pl-2">
                  {children.map((c) => {
                    const isRowSel = selectedCauseId === c.id;
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => {
                            const causeCat = categoryOf(c);
                            // Se la causale è in una categoria diversa da quella filtrata,
                            // allineo il filtro così che la riga sia visibile e lo scroll funzioni.
                            if (selectedCategory && selectedCategory !== causeCat) {
                              setSelectedCategory(causeCat);
                            }
                            // Se i filtri provenienza/stato la nascondono, li resetto.
                            const prov = provenanceOf(c);
                            if (provenanceFilter !== "all" && provenanceFilter !== prov) {
                              setProvenanceFilter("all");
                            }
                            if (statusFilter === "active" && !c.active) setStatusFilter("all");
                            if (statusFilter === "suspended" && c.active) setStatusFilter("all");
                            setSelectedCauseId(c.id);
                          }}
                          data-testid={`cause-nav-item-${c.code}`}
                          className={`w-full text-left px-2 py-1 rounded text-[13px] truncate ${
                            isRowSel
                              ? "bg-suite-primary/10 text-suite-primary font-medium"
                              : "text-slate-600 hover:bg-slate-100"
                          }`}
                          title={c.label}
                        >
                          {c.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );

  return (
    <div className="flex flex-col md:flex-row gap-4">
      {/* Colonna sinistra: navigazione gerarchica */}
      <aside className="hidden md:block md:w-[240px] shrink-0">
        <SuiteCard padding="sm" testid="causes-nav-card">
          {NavTree}
        </SuiteCard>
      </aside>

      {/* Accordion navigazione per viewport stretto */}
      <div className="md:hidden">
        <button
          type="button"
          onClick={() => setMobileNavOpen((v) => !v)}
          className="w-full px-3 py-2 text-sm font-medium bg-white border border-slate-200 rounded-md flex items-center justify-between"
          aria-expanded={mobileNavOpen}
          data-testid="cause-nav-mobile-toggle"
        >
          <span>Categorie {selectedCategory ? `· ${categoryLabel(selectedCategory)}` : "· Tutte"}</span>
          {mobileNavOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        {mobileNavOpen && (
          <SuiteCard padding="sm" className="mt-2" testid="causes-nav-mobile">
            {NavTree}
          </SuiteCard>
        )}
      </div>

      {/* Colonna destra: filtri + tabella */}
      <div className="flex-1 min-w-0">
        {/* Filtri superiori */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-suite-neutral-400 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome, codice o categoria…"
              className="h-9 pl-9 pr-9 bg-white"
              data-testid="cause-search-input"
              aria-label="Cerca causale"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                aria-label="Cancella ricerca causali"
                data-testid="cause-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <Select value={provenanceFilter} onValueChange={setProvenanceFilter}>
            <SelectTrigger className="h-9 w-full lg:w-[180px] bg-white" data-testid="cause-filter-provenance" aria-label="Filtro provenienza">
              <SelectValue placeholder="Provenienza" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le provenienze</SelectItem>
              <SelectItem value="system">Sistema</SelectItem>
              <SelectItem value="global">Globali</SelectItem>
              <SelectItem value="company">Aziendali</SelectItem>
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-full lg:w-[150px] bg-white" data-testid="cause-filter-status" aria-label="Filtro stato">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="active">Attive</SelectItem>
              <SelectItem value="suspended">Sospese</SelectItem>
            </SelectContent>
          </Select>

          {(isSuper || isCompanyAdmin) && (
            <SuiteButton onClick={openNew} icon={Plus} data-testid="new-cause-btn">
              Nuova causale
            </SuiteButton>
          )}
        </div>

        {/* Tabella compatta */}
        <SuiteCard padding="none" testid="causes-catalog-card">
          <div className="max-h-[calc(100vh-260px)] overflow-auto">
            <table className="w-full text-sm" data-testid="causes-catalog-table">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-3 py-2 font-semibold w-[26%]">Causale</th>
                  <th className="px-3 py-2 font-semibold w-[10%]">Codice</th>
                  <th className="px-3 py-2 font-semibold w-[13%]">Provenienza</th>
                  <th className="px-3 py-2 font-semibold w-[27%]">Regole principali</th>
                  <th className="px-3 py-2 font-semibold w-[7%] text-center" title="Causale attiva o sospesa">Attiva</th>
                  <th className="px-3 py-2 font-semibold w-[7%] text-center" title="Visibile ai clienti aziendali">Visibile ai clienti</th>
                  <th className="px-3 py-2 font-semibold w-[10%] text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredForTable.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-slate-500" data-testid="cause-search-empty">
                      Nessuna causale corrisponde ai filtri correnti.
                    </td>
                  </tr>
                )}
                {filteredForTable.map((c) => {
                  const prov = provenanceOf(c);
                  const provMeta = PROVENANCE_META[prov];
                  const ProvIcon = provMeta.icon;
                  const badges = ruleBadges(c, rangeDayModeLabels, effectiveRangeDayMode);
                  const shownBadges = badges.slice(0, 3);
                  const extraBadges = badges.slice(3);
                  const isSel = selectedCauseId === c.id;

                  return (
                    <tr
                      key={c.id}
                      ref={(el) => { if (el) rowRefs.current.set(c.id, el); }}
                      data-testid={`cause-row-${c.code}`}
                      className={`border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 ${
                        isSel ? "bg-suite-primary/5" : ""
                      }`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900 truncate">{c.label}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="font-mono text-[12px] text-slate-500">{c.display_code || c.code}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] border ${provMeta.badge}`}
                              data-testid={`cause-provenance-${c.code}`}>
                          <ProvIcon size={11} aria-hidden />
                          <span>{provMeta.label}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1 items-center">
                          {shownBadges.map((b) => (
                            <span key={b.key} className={`px-1.5 py-0.5 rounded text-[11px] border ${b.css}`}>{b.text}</span>
                          ))}
                          {extraBadges.length > 0 && (
                            <span
                              className="px-1.5 py-0.5 rounded text-[11px] border bg-slate-100 text-slate-600 border-slate-200 cursor-help"
                              title={extraBadges.map((b) => b.text).join(" · ")}
                              data-testid={`cause-rules-more-${c.code}`}
                            >
                              +{extraBadges.length}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Switch
                          checked={c.active}
                          onCheckedChange={() => toggleActive(c)}
                          disabled={!canManageCause(c)}
                          aria-label={canManageCause(c) ? `${c.active ? "Sospendi" : "Attiva"} causale ${c.label}` : "Causale in sola lettura"}
                          data-testid={`toggle-active-${c.code}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Switch
                          checked={c.available_to_clients}
                          onCheckedChange={() => toggleClient(c)}
                          disabled={!canManageCause(c)}
                          aria-label={canManageCause(c) ? `${c.available_to_clients ? "Nascondi ai clienti" : "Mostra ai clienti"} causale ${c.label}` : "Causale in sola lettura"}
                          data-testid={`toggle-client-${c.code}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          {isSuper && c.scope === "company" && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => promoteGlobal(c)}
                              title="Promuovi a causale globale"
                              aria-label={`Promuovi ${c.label} a globale`}
                              data-testid={`promote-cause-${c.code}`}
                            >
                              <Upload size={14} />
                            </Button>
                          )}
                          {canManageCause(c) && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => openEdit(c)}
                              title="Modifica causale"
                              aria-label={`Modifica ${c.label}`}
                              data-testid={`edit-cause-${c.code}`}
                            >
                              <Pencil size={14} />
                            </Button>
                          )}
                          {canManageCause(c) && !c.system && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => remove(c)}
                              className="text-red-700 hover:bg-red-50"
                              title="Elimina causale"
                              aria-label={`Elimina ${c.label}`}
                              data-testid={`delete-cause-${c.code}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SuiteCard>
      </div>
    </div>
  );
}
